'use client';

import { Loader2 } from 'lucide-react';

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'src/components';
import { Progress } from 'src/components/ui/progress';

import { UseExportReturn } from './use-export';

export type ExporterProps = UseExportReturn & {
  filename?: string;
};

export function Exporter({
  open,
  onOpenChange,
  loading,
  progress,
  handleExport,
  totalRows,
  /** 组件属性 */
  filename = 'export',
}: ExporterProps) {
  const isCompleted = !loading && progress === 100;

  const handleClose = () => {
    if (!loading) {
      onOpenChange(false);
    }
  };

  const handleConfirm = () => {
    if (isCompleted) {
      handleClose();
    } else if (!loading) {
      handleExport(filename);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>导出数据</DialogTitle>
          <DialogDescription>
            {loading
              ? '正在导出数据，请稍候...'
              : isCompleted
                ? '导出完成！'
                : totalRows && totalRows > 0
                  ? `即将导出 ${totalRows.toLocaleString()} 行数据`
                  : `没有可导出的数据`}
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">导出进度</span>
                <span className="font-medium">{progress}%</span>
              </div>
              <Progress value={progress} className="h-2" />
            </div>
            <p className="text-sm text-muted-foreground text-center">
              正在生成 Excel 文件，请勿关闭此窗口...
            </p>
          </div>
        )}

        {isCompleted && (
          <div className="py-4 text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-green-100 mb-4">
              <svg
                className="w-6 h-6 text-green-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
            <p className="text-sm text-muted-foreground">文件已成功下载到您的下载文件夹</p>
          </div>
        )}

        <DialogFooter>
          {!isCompleted && (
            <Button variant="outline" onClick={handleClose} disabled={loading}>
              取消
            </Button>
          )}
          <Button
            onClick={handleConfirm}
            disabled={!totalRows || totalRows === 0 || (loading && !isCompleted)}
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                导出中...
              </>
            ) : isCompleted ? (
              '完成'
            ) : (
              '确定'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
